import React, { useState, useMemo, useCallback } from "react";
import moment from "moment";

import Datatable from "../../../core/common/dataTable";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import StatCard from "../adminDashboard/components/StatsCard";

type Props = {
    employeeData: any;
    selectedCompany: any;
    isLoading: boolean;
    onRefresh: () => void;
};
type Gender = "all" | "M" | "F" | "O";
const HeadCountReport: React.FC<Props> = ({
    employeeData,
    selectedCompany,
    isLoading,
    onRefresh
}) => {
    const [selectedEmployeeStatus, setSelectedEmployeeStatus] = useState("all");
    const [selectedGender, setSelectedGender] = useState<Gender>("all");
    const [selectedDepartment, setSelectedDepartment] = useState("all");
    const [selectedDesignation, setSelectedDesignation] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStartDate, setSelectedStartDate] = useState<moment.Moment | null>(null);
    const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | null>(null);

    const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const genderLabels: Record<Gender, string> = {
        all: "All",
        M: "Male",
        F: "Female",
        O: "Other",
    };

    const filteredEmployees = useMemo(() => {
        if (!employeeData?.employeeDetails?.employees) return [];

        let filtered = employeeData.employeeDetails.employees;

        if (selectedEmployeeStatus !== "all") {
            filtered = filtered.filter((emp: any) =>
                emp.employeeStatus.toLowerCase() === selectedEmployeeStatus.toLowerCase()
            );
        }

        if (selectedGender !== "all") {
            filtered = filtered.filter((emp: any) =>
                emp.gender?.toLowerCase() === selectedGender.toLowerCase()
            );
        }

        if (selectedDepartment !== "all") {
            filtered = filtered.filter((emp: any) =>
                emp.department === selectedDepartment
            );
        }

        if (selectedDesignation !== "all") {
            filtered = filtered.filter((emp: any) =>
                emp.designation === selectedDesignation
            );
        }

        if (selectedStartDate && selectedEndDate) {
            filtered = filtered.filter((emp: any) => {
                const joiningDate = moment(emp.dateOfJoining, "DD-MM-YYYY");
                return joiningDate.isBetween(selectedStartDate, selectedEndDate, 'day', '[]');
            });
        }

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter((emp: any) =>
                emp.employeeName?.toLowerCase().includes(searchLower) ||
                emp.esslId?.toLowerCase().includes(searchLower) ||
                emp.designation?.toLowerCase().includes(searchLower) ||
                emp.department?.toLowerCase().includes(searchLower) ||
                emp.email?.toLowerCase().includes(searchLower) ||
                emp.employeeId?.toLowerCase().includes(searchLower)
            );
        }

        return filtered;
    }, [employeeData, selectedEmployeeStatus, selectedGender, selectedDepartment, selectedDesignation, selectedStartDate, selectedEndDate, searchTerm]);

    const uniqueDepartments = useMemo<string[]>(() => {
        if (!employeeData?.employeeDetails?.employees) return [];

        return Array.from(
            new Set<string>(
                employeeData.employeeDetails.employees
                    .map((emp: any) => emp.department as string)
                    .filter(Boolean)
            )
        );
    }, [employeeData]);

    const handleReset = () => {
        setSelectedEmployeeStatus("all");
        setSelectedGender("all");
        setSelectedDepartment("all");
        setSelectedDesignation("all");
        setSearchTerm("");
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setSelectedRowKeys([]);
        setIsSearchActive(false);
    };

    const exportToCSV = useCallback(() => {
        if (!filteredEmployees?.length) return;

        setIsExporting(true);

        try {
            const csvLines: string[] = [];

            // 🔹 Header Info
            csvLines.push(`"Headcount Report Export"`);
            csvLines.push(`"Company","${selectedCompany?.name || "N/A"}"`);
            csvLines.push(`"Exported At","${moment().format("YYYY-MM-DD HH:mm:ss")}"`);
            csvLines.push("");

            /* ===============================
               EMPLOYEE DETAILS
            =============================== */
            csvLines.push(`"=== EMPLOYEE DETAILS ==="`);

            const employeeHeaders = [
                "Employee Name",
                "Email",
                "ESSL ID",
                "Employee ID",
                "Designation",
                "Department",
                "Reporting Officer",
                "Date of Joining",
                "Tenure (Days)",
                "Tenure Status",
                "Employee Status",
                "Gender",
                "Work Mode",
                "Biometric Status"
            ];

            csvLines.push(employeeHeaders.map(h => `"${h}"`).join(","));

            filteredEmployees.forEach((emp: any) => {
                csvLines.push([
                    emp.employeeName || "",
                    emp.email || "",
                    emp.esslId || "",
                    emp.employeeId || "",
                    emp.designation || "",
                    emp.department || "",
                    emp.reportingOfficer || "",
                    emp.dateOfJoining || "",
                    emp.tenure || "",
                    emp.tenureStatus || "",
                    emp.employeeStatus || "",
                    emp.gender || "",
                    emp.workFrom || "",
                    emp.biometricEnabled || ""
                ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
            });

            csvLines.push("");

            /* ===============================
               DEPARTMENT STATISTICS
            =============================== */
            csvLines.push(`"=== DEPARTMENT STATISTICS ==="`);

            const deptHeaders = [
                "Department",
                "Total Employees",
                "Active Employees",
                "Inactive Employees",
                "Suspended Employees",
                "% of Total"
            ];

            csvLines.push(deptHeaders.map(h => `"${h}"`).join(","));

            employeeData?.departmentStatistics?.departments?.forEach((dept: any) => {
                const percentage = employeeData?.statistics?.raw?.current?.totalEmployees > 0
                    ? ((dept.totalEmployees / employeeData.statistics.raw.current.totalEmployees) * 100).toFixed(1) + "%"
                    : "0%";

                csvLines.push([
                    dept.departmentName || "",
                    dept.totalEmployees || 0,
                    dept.activeEmployees || 0,
                    dept.inactiveEmployees || 0,
                    dept.suspendedEmployees || 0,
                    percentage
                ].map(v => `"${v}"`).join(","));
            });

            csvLines.push("");

            /* ===============================
               DESIGNATION STATISTICS
            =============================== */
            csvLines.push(`"=== DESIGNATION STATISTICS ==="`);

            const desigHeaders = [
                "Designation",
                "Total Employees",
                "Active Employees",
                "Inactive Employees",
                "Suspended Employees",
                "% of Total"
            ];

            csvLines.push(desigHeaders.map(h => `"${h}"`).join(","));

            employeeData?.designationStatistics?.designations?.forEach((desig: any) => {
                const percentage = employeeData?.statistics?.raw?.current?.totalEmployees > 0
                    ? ((desig.totalEmployees / employeeData.statistics.raw.current.totalEmployees) * 100).toFixed(1) + "%"
                    : "0%";

                csvLines.push([
                    desig.designationName || "",
                    desig.totalEmployees || 0,
                    desig.activeEmployees || 0,
                    desig.inactiveEmployees || 0,
                    desig.suspendedEmployees || 0,
                    percentage
                ].map(v => `"${v}"`).join(","));
            });

            /* ===============================
               DOWNLOAD FILE
            =============================== */
            const csvContent = csvLines.join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `Headcount_Report_${selectedCompany?.name?.replace(/\s+/g, "_") || "Company"}_${moment().format("YYYY-MM-DD_HH-mm")}.csv`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error("CSV Export Error:", error);
        } finally {
            setIsExporting(false);
        }
    }, [filteredEmployees, employeeData, selectedCompany]);

    const columns = useMemo(() => [
        {
            title: 'Employee',
            dataIndex: 'employeeName',
            key: 'employeeName',
            width: 250,
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center">
                    <div className="avatar avatar-sm me-3">
                        <ImageWithBasePath
                            src={record.profilePic || "assets/img/users/user.png"}
                            className="img-fluid rounded-circle"
                            alt="employee"
                        />

                    </div>
                    <div>
                        <h6 className="mb-0">{text}</h6>
                        <small className="text-muted">{record.email}</small>
                    </div>
                </div>
            ),
            sorter: (a: any, b: any) => a.employeeName?.localeCompare(b.employeeName),
        },
        {
            title: 'ESSL ID',
            dataIndex: 'esslId',
            key: 'esslId',
            width: 120,
            sorter: (a: any, b: any) => a.esslId?.localeCompare(b.esslId),
        },
        {
            title: 'Employee ID',
            dataIndex: 'employeeId',
            key: 'employeeId',
            width: 120,
            sorter: (a: any, b: any) => a.employeeId?.localeCompare(b.employeeId),
        },
        {
            title: 'Designation',
            dataIndex: 'designation',
            key: 'designation',
            width: 180,
            sorter: (a: any, b: any) => a.designation?.localeCompare(b.designation),
            render: (text: string) => (
                <span className="badge bg-primary-light text-primary">{text}</span>
            ),
        },
        {
            title: 'Department',
            dataIndex: 'department',
            key: 'department',
            width: 180,
            sorter: (a: any, b: any) => a.department?.localeCompare(b.department),
            render: (text: string) => (
                <span className="badge bg-info-light text-info">{text}</span>
            ),
        },
        {
            title: 'Reporting Officer',
            dataIndex: 'reportingOfficer',
            key: 'reportingOfficer',
            width: 200,
            sorter: (a: any, b: any) => a.reportingOfficer?.localeCompare(b.reportingOfficer),
        },
        {
            title: 'Date of Joining',
            dataIndex: 'dateOfJoining',
            key: 'dateOfJoining',
            width: 130,
            sorter: (a: any, b: any) => {
                const dateA = moment(a.dateOfJoining, 'DD-MM-YYYY');
                const dateB = moment(b.dateOfJoining, 'DD-MM-YYYY');
                return dateA.valueOf() - dateB.valueOf();
            },
        },
        {
            title: 'Tenure',
            dataIndex: 'tenure',
            key: 'tenure',
            width: 120,
            sorter: (a: any, b: any) => {
                const daysA = parseInt(a.tenure) || 0;
                const daysB = parseInt(b.tenure) || 0;
                return daysA - daysB;
            },
            render: (text: string, record: any) => (
                <span className={`badge ${record.tenureStatus === 'Active' ? 'bg-success-light text-success' : record.tenureStatus === 'Suspended' ? 'bg-warning-light text-warning' : 'bg-danger-light text-danger'}`}>
                    {text}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'employeeStatus',
            key: 'employeeStatus',
            width: 100,
            sorter: (a: any, b: any) => a.employeeStatus?.localeCompare(b.employeeStatus),
            render: (text: string) => (
                <span className={`badge ${text === 'Active' ? 'bg-success' : text === 'Suspended' ? 'bg-warning' : 'bg-danger'}`}>
                    {text}
                </span>
            ),
        },
        {
            title: 'Gender',
            dataIndex: 'gender',
            key: 'gender',
            width: 100,
            sorter: (a: any, b: any) => a.gender?.localeCompare(b.gender),
            render: (text: string) => (
                <span className={`badge ${text === 'Male' ? 'bg-info' :
                    text === 'Female' ? 'bg-warning' :
                        'bg-secondary'
                    }`}>
                    {text}
                </span>
            ),
        },
        {
            title: 'Work Mode',
            dataIndex: 'workFrom',
            key: 'workFrom',
            width: 140,
            sorter: (a: any, b: any) => a.workFrom?.localeCompare(b.workFrom),
            render: (text: string) => (
                <span className={`badge ${text === 'Work From Office' ? 'bg-primary' :
                    text === 'Work From Home' ? 'bg-success' :
                        'bg-secondary'
                    }`}>
                    {text}
                </span>
            ),
        },
        {
            title: 'Biometric',
            dataIndex: 'biometricEnabled',
            key: 'biometricEnabled',
            width: 100,
            sorter: (a: any, b: any) => a.biometricEnabled?.localeCompare(b.biometricEnabled),
            render: (text: string) => (
                <span className={`badge ${text === 'Enabled' ? 'bg-success' :
                    text === 'Disabled' ? 'bg-danger' :
                        'bg-secondary'
                    }`}>
                    {text}
                </span>
            ),
        },

    ], []);

    const departmentColumns = useMemo(() => [
        {
            title: 'Department',
            dataIndex: 'departmentName',
            key: 'departmentName',
            width: 200,
            sorter: (a: any, b: any) => a.departmentName?.localeCompare(b.departmentName),
        },
        {
            title: 'Total',
            dataIndex: 'totalEmployees',
            key: 'totalEmployees',
            width: 80,
            sorter: (a: any, b: any) => a.totalEmployees - b.totalEmployees,
            render: (text: number) => <span className="fw-semibold">{text}</span>
        },
        {
            title: 'Active',
            dataIndex: 'activeEmployees',
            key: 'activeEmployees',
            width: 90,
            sorter: (a: any, b: any) => a.activeEmployees - b.activeEmployees,
            render: (text: number) => (
                <span className="badge bg-success-light text-success">{text}</span>
            )
        },
        {
            title: 'Inactive',
            dataIndex: 'inactiveEmployees',
            key: 'inactiveEmployees',
            width: 90,
            sorter: (a: any, b: any) => a.inactiveEmployees - b.inactiveEmployees,
            render: (text: number) => (
                <span className="badge bg-danger-light text-danger">{text}</span>
            )
        },
        {
            title: 'Suspended',
            dataIndex: 'suspendedEmployees',
            key: 'suspendedEmployees',
            width: 100,
            sorter: (a: any, b: any) => (a.suspendedEmployees || 0) - (b.suspendedEmployees || 0),
            render: (text: number) => (
                <span className="badge bg-warning-light text-warning">{text || 0}</span>
            )
        },
        {
            title: '% of Total',
            dataIndex: 'percentage',
            key: 'percentage',
            width: 100,
            sorter: (a: any, b: any) => a.percentage - b.percentage,
            render: (text: string) => (
                <span className="badge bg-primary-light text-primary">{text}</span>
            )
        }
    ], []);

    const designationColumns = useMemo(() => [
        {
            title: 'Designation',
            dataIndex: 'designationName',
            key: 'designationName',
            width: 200,
            sorter: (a: any, b: any) => a.designationName?.localeCompare(b.designationName),
        },
        {
            title: 'Total',
            dataIndex: 'totalEmployees',
            key: 'totalEmployees',
            width: 80,
            sorter: (a: any, b: any) => a.totalEmployees - b.totalEmployees,
            render: (text: number) => <span className="fw-semibold">{text}</span>
        },
        {
            title: 'Active',
            dataIndex: 'activeEmployees',
            key: 'activeEmployees',
            width: 90,
            sorter: (a: any, b: any) => a.activeEmployees - b.activeEmployees,
            render: (text: number) => (
                <span className="badge bg-success-light text-success">{text}</span>
            )
        },
        {
            title: 'Inactive',
            dataIndex: 'inactiveEmployees',
            key: 'inactiveEmployees',
            width: 90,
            sorter: (a: any, b: any) => a.inactiveEmployees - b.inactiveEmployees,
            render: (text: number) => (
                <span className="badge bg-danger-light text-danger">{text}</span>
            )
        },
        {
            title: 'Suspended',
            dataIndex: 'suspendedEmployees',
            key: 'suspendedEmployees',
            width: 100,
            sorter: (a: any, b: any) => (a.suspendedEmployees || 0) - (b.suspendedEmployees || 0),
            render: (text: number) => (
                <span className="badge bg-warning-light text-warning">{text || 0}</span>
            )
        },
        {
            title: '% of Total',
            dataIndex: 'percentage',
            key: 'percentage',
            width: 100,
            sorter: (a: any, b: any) => a.percentage - b.percentage,
            render: (text: string) => (
                <span className="badge bg-primary-light text-primary">{text}</span>
            )
        }
    ], []);

    const renderStatCards = useMemo(() => {
        if (!employeeData?.statistics?.cards) return null;

        const stats = employeeData.statistics.cards;

        const LAST_KEYS = ["activeEmployees", "inactiveEmployees", "suspendedEmployees", "newJoiners"] as const;

        const cardKeys = Object.keys(stats).sort((a, b) => {
            const aIndex = LAST_KEYS.indexOf(a as any);
            const bIndex = LAST_KEYS.indexOf(b as any);

            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }

            if (aIndex !== -1) return 1;

            if (bIndex !== -1) return -1;

            return 0;
        });
        return (
            <div className="row mb-4 g-3">
                {cardKeys.map((key) => {
                    const stat = stats[key];

                    return (
                        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12" key={key}>
                            <div className="h-100">
                                <StatCard
                                    title={stat.title}
                                    value={stat.value}
                                    change={stat.change}
                                    changeLabel="vs last month"
                                    trend={stat.trend}
                                    icon={stat.icon}
                                    description={stat.description}
                                    variant={stat.variant}
                                    module=""
                                    valueFormat={stat.valueFormat}
                                    genderBreakdown={
                                        LAST_KEYS.includes(key as any)
                                            ? {
                                                male: stat.genderBreakdown?.male || 0,
                                                female: stat.genderBreakdown?.female || 0,
                                                other: stat.genderBreakdown?.other || 0,
                                            }
                                            : undefined
                                    }
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }, [employeeData]);

    return (
        <div className="">
            {renderStatCards}

            <div className="card mb-4" style={{ position: "relative", zIndex: 1050 }}>
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h5 className="mb-0">Filters & Search</h5>
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        {/* Status Filter */}
                        <div className="dropdown">
                            <button
                                className="btn btn-outline-primary dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <i className="ti ti-filter me-2"></i>
                                Status: {selectedEmployeeStatus === "all" ? "All" : selectedEmployeeStatus}
                            </button>
                            <ul className="dropdown-menu">
                                <li><button className="dropdown-item" onClick={() => setSelectedEmployeeStatus("all")}>All Status</button></li>
                                <li><button className="dropdown-item" onClick={() => setSelectedEmployeeStatus("Active")}>Active</button></li>
                                <li><button className="dropdown-item" onClick={() => setSelectedEmployeeStatus("Inactive")}>Inactive</button></li>
                                <li><button className="dropdown-item" onClick={() => setSelectedEmployeeStatus("Suspended")}>Suspended</button></li>
                            </ul>
                        </div>

                        {/* Gender Filter */}
                        <div className="dropdown">
                            <button
                                className="btn btn-outline-info dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <i className="ti ti-gender-bigender me-2"></i>
                                Gender: {genderLabels[selectedGender]}
                            </button>
                            <ul className="dropdown-menu">
                                <li><button className="dropdown-item" onClick={() => setSelectedGender("all")}>All Gender</button></li>
                                <li><button className="dropdown-item" onClick={() => setSelectedGender("M")}>Male</button></li>
                                <li><button className="dropdown-item" onClick={() => setSelectedGender("F")}>Female</button></li>
                                <li><button className="dropdown-item" onClick={() => setSelectedGender("O")}>Other</button></li>
                            </ul>
                        </div>

                        {/* Department Filter */}
                        {uniqueDepartments.length > 0 && (
                            <div className="dropdown">
                                <button
                                    className="btn btn-outline-success dropdown-toggle"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    <i className="ti ti-building me-2"></i>
                                    Department: {selectedDepartment === "all" ? "All" : selectedDepartment.substring(0, 15) + (selectedDepartment.length > 15 ? "..." : "")}
                                </button>
                                <ul className="dropdown-menu" style={{ maxHeight: "300px", overflowY: "auto" }}>
                                    <li><button className="dropdown-item" onClick={() => setSelectedDepartment("all")}>All Departments</button></li>
                                    {uniqueDepartments.map(dept => (
                                        <li key={dept}><button className="dropdown-item" onClick={() => setSelectedDepartment(dept)}>{dept}</button></li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Search Input */}
                        <div className="input-group" style={{ width: "250px" }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search employees..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <button className="btn btn-outline-secondary" type="button">
                                <i className="ti ti-search" />
                            </button>
                        </div>

                        {/* Export Button */}
                        <button
                            className="btn btn-success"
                            onClick={exportToCSV}
                            disabled={isExporting || !filteredEmployees.length}
                            title="Export employee data"
                        >
                            {isExporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <i className="ti ti-download me-2"></i>
                                    Export Data
                                </>
                            )}
                        </button>

                        {/* Reset Button */}
                        <button className="btn btn-danger" onClick={handleReset}>
                            <i className="ti ti-refresh me-2"></i>
                            Reset Filters
                        </button>
                    </div>
                </div>
            </div>

            <div className="card mb-4">
                <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">
                        Employee Details ({filteredEmployees.length} employees)
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                        {selectedRowKeys.length > 0 && (
                            <span className="text-muted">
                                {selectedRowKeys.length} selected
                            </span>
                        )}
                        {isSearchActive && (
                            <span className="badge bg-warning">
                                <i className="ti ti-search me-1"></i>
                                Search Active
                            </span>
                        )}
                    </div>
                </div>
                <div className="card-body p-0">
                    <Datatable
                        columns={columns}
                        dataSource={filteredEmployees}
                        Selection={false}
                        onFilterStatusChange={setIsSearchActive}
                        selectedRowKeysParent={setSelectedRowKeys}
                    />
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-lg-6">
                    <div className="card h-100">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="mb-0">Department Statistics</h5>
                            <span className="badge bg-primary">
                                {employeeData?.departmentStatistics?.departmentCount || 0} Departments
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <Datatable
                                columns={departmentColumns}
                                dataSource={employeeData?.departmentStatistics?.departments?.map((dept: any) => ({
                                    ...dept,
                                    percentage: employeeData?.statistics?.raw?.current?.totalEmployees > 0
                                        ? `${((dept.totalEmployees / employeeData.statistics.raw.current.totalEmployees) * 100).toFixed(1)}%`
                                        : '0%'
                                })) || []}
                                Selection={false}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card h-100">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="mb-0">Designation Statistics</h5>
                            <span className="badge bg-primary">
                                {employeeData?.designationStatistics?.designationCount || 0} Designations
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <Datatable
                                columns={designationColumns}
                                dataSource={employeeData?.designationStatistics?.designations?.map((desig: any) => ({
                                    ...desig,
                                    percentage: employeeData?.statistics?.raw?.current?.totalEmployees > 0
                                        ? `${((desig.totalEmployees / employeeData.statistics.raw.current.totalEmployees) * 100).toFixed(1)}%`
                                        : '0%'
                                })) || []}
                                Selection={false}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeadCountReport;